import { useEffect, useState } from "react";
import axios from "axios";
import { Divider } from "@mui/material";
import localize from "../../../../common/localize";
import useGlobalContext from "../../../../hooks/useGlobalContext";
import ReportImageFieldValue from "./ReportImageFieldValue";
import DynamicFormSignature from "./DynamicFormSignature";
import { FormResultsFormatEnum } from "../../../../common/EntitiesManagement";
import PageTitleSubtitle from "../../../../components/PageTitleSubTitle";

export default function DynamicFormPageForReport({
  page,
  eventId,
  statusChangeId,
  originalFieldValue,
  format,
  hideModificationDate,
}) {
  const { config, DYNAMIC_FORM_ENDPOINT, EVENTS_API } = useGlobalContext();
  const [fields, setFields] = useState(null);
  const [values, setValues] = useState(null);
  const [fieldValues, setFieldValues] = useState(null);

  const getDynamicFormPageFields = async () => {
    try {
      const data = await axios.get(
        `${DYNAMIC_FORM_ENDPOINT}/${page?.DynamicForm?.Id}/page/${page?.Id}/field`,
        config,
      );

      setFields(data?.data?.Data);
    } catch (error) {
      setFields([]);
    }
  };

  const getDynamicFormFieldValues = async () => {
    try {
      const data = await axios.get(
        `${EVENTS_API}/${eventId}/page/${page?.Id}/status-change/${statusChangeId}/dynamic-form-field-values`,
        config,
      );
      setValues(data?.data?.Data);
    } catch (error) {
      setValues([]);
    }
  };

  const getDynamicFormFieldValuesWithoutStatusChange = async () => {
    try {
      const data = await axios.get(
        `${EVENTS_API}/${eventId}/page/${page?.Id}/no-status-change/dynamic-form-field-values`,
        config,
      );
      setValues(data?.data?.Data);
    } catch (error) {
      setValues([]);
    }
  };

  useEffect(() => {
    setValues([]);

    if (page?.DynamicForm) {
      getDynamicFormPageFields();
      if (statusChangeId) {
        getDynamicFormFieldValues();
      } else {
        getDynamicFormFieldValuesWithoutStatusChange();
      }
    }
  }, [page]);

  const getBooleanFieldText = (options, checked) => {
    if (options) {
      const optionsTexts = JSON.parse(options);
      if (optionsTexts) {
        return checked ? optionsTexts.trueText : optionsTexts.falseText;
      }
    }

    return checked ? localize.yes : localize.no;
  };

  const getFieldValue = (fieldValue) => {
    if (fieldValue?.FormField?.ValueType === "BOOLEAN") {
      return getBooleanFieldText(
        fieldValue?.FormField?.Options,
        fieldValue?.FieldValue?.Value === "1",
      );
    }

    if (
      !fieldValue?.FieldValue?.Value ||
      fieldValue?.FieldValue?.Value === ""
    ) {
      return localize.no_data;
    }

    return fieldValue?.FieldValue?.Value;
  };

  useEffect(() => {
    if (fields && fields.length > 0) {
      setFieldValues(fields);

      return;
    }

    setFieldValues([]);
  }, [fields]);

  const constructWithFieldValue = (field) => {
    const formFieldValue = values?.find(
      (x) => x?.FormFieldOnPage?.FormField?.Id === field?.FormField?.Id,
    );

    return {
      ...field,
      FieldValue: {
        Id: formFieldValue?.Id,
        Value: originalFieldValue
          ? formFieldValue?.OriginalFieldValue &&
            formFieldValue?.OriginalFieldValue !== ""
            ? formFieldValue?.OriginalFieldValue
            : localize.no_data
          : formFieldValue?.FieldValue,
        OriginalValue: formFieldValue?.OriginalFieldValue,
        ModifiedBy: formFieldValue?.ModifiedBy,
        ModificationDate: formFieldValue?.ModificationDate,
        FromWeb: formFieldValue?.FromWeb,
      },
    };
  };

  const isPhotoOrSignature = (valueType) =>
    isPhoto(valueType) || isSignature(valueType);

  const isPhoto = (valueType) => valueType === "PHOTO";
  const isSignature = (valueType) => valueType === "SIGNATURE";

  const generateDynamicFormFieldsOnPageValue = () => {
    const currentFields = fieldValues;
    setFieldValues(currentFields.map((x) => constructWithFieldValue(x)));
  };

  useEffect(() => {
    if (values && values.length > 0) {
      generateDynamicFormFieldsOnPageValue();
    }
  }, [values]);

  const getFieldValuePanel = (fieldValue) => {
    if (!isPhotoOrSignature(fieldValue?.FormField?.ValueType)) {
      return (
        <label className="report_little_text">
          {getFieldValue(fieldValue)}
        </label>
      );
    }

    if (isSignature(fieldValue?.FormField?.ValueType)) {
      return (
        <DynamicFormSignature
          key={fieldValue?.FormField?.Id}
          isOriginal={originalFieldValue}
          signatureField={{
            FormField: fieldValue?.FormField,
            FieldValue: fieldValue?.FieldValue,
          }}
        />
      );
    }

    if (isPhoto(fieldValue?.FormField?.ValueType)) {
      return (
        <ReportImageFieldValue
          valueId={fieldValue?.FieldValue?.Id}
          original={originalFieldValue}
        />
      );
    }
  };

  const getResultsComponent = (fieldValue) => {
    const showTwoColumns =
      format && format.Id === FormResultsFormatEnum.TwoColumns;

    return (
      <div
        className={`flex w-full items-center report_internal_field ${
          showTwoColumns ? "" : "flex-col"
        }`}
      >
        <div
          className="flex flex-col"
          style={{
            width: showTwoColumns ? "50%" : "100%",
            padding: "5px",
          }}
        >
          <PageTitleSubtitle
            title={`${fieldValue?.FormField?.Title}:`}
            subtitle={fieldValue?.FormField?.Description}
            useEventTitleStyle
            useFieldTitleStyle
          />
        </div>
        <div
          className="flex flex-col"
          style={{ width: showTwoColumns ? "50%" : "100%", padding: "5px" }}
        >
          {getFieldValuePanel(fieldValue)}

          {!hideModificationDate &&
            !originalFieldValue &&
            fieldValue?.FieldValue?.FromWeb && (
              <label className="modification_label">{`${
                localize.modified_by
              }: ${fieldValue?.FieldValue?.ModifiedBy}, ${new Date(
                fieldValue?.FieldValue?.ModificationDate,
              ).toLocaleString(undefined, {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })}`}</label>
            )}
        </div>
      </div>
    );
  };

  return (
    <div>
      {fieldValues?.length > 0 ? (
        fieldValues.map((fieldValue) => (
          <div key={fieldValue.Id} className="w-full">
            {getResultsComponent(fieldValue)}
          </div>
        ))
      ) : (
        <>
          <Divider />
          <label className="page_description_report">
            {localize.no_form_fields}
          </label>
        </>
      )}
    </div>
  );
}
